/**
 * TenantSwitcher.jsx — Compact dropdown showing the caller's active
 * tenant and allowing fast context switches. Designed to sit in the
 * topbar; self-contained (no external styling deps beyond Tailwind).
 */
import React, { useState, useRef, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { Building2, Check, ChevronDown, Shield } from 'lucide-react';

const ROLE_TONE = {
  owner:  'text-amber-700 bg-amber-50 border-amber-200',
  admin:  'text-indigo-700 bg-indigo-50 border-indigo-200',
  member: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  viewer: 'text-slate-600 bg-slate-100 border-slate-200',
};

export default function TenantSwitcher() {
  const { memberships, activeTenant, activeId, switchTenant, isPlatformAdmin, loading } = useTenant();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (loading) {
    return (
      <div
        data-testid="tenant-switcher-loading"
        className="inline-flex items-center gap-2 text-xs text-slate-500"
      >
        <Building2 className="w-3.5 h-3.5 animate-pulse" /> loading…
      </div>
    );
  }

  if (memberships.length === 0 && !isPlatformAdmin) {
    return (
      <div
        data-testid="tenant-switcher-empty"
        className="inline-flex items-center gap-2 text-xs text-slate-500"
      >
        <Building2 className="w-3.5 h-3.5" /> No tenants
      </div>
    );
  }

  return (
    <div ref={ref} className="relative" data-testid="tenant-switcher">
      <button
        data-testid="tenant-switcher-button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium"
      >
        <Building2 className="w-4 h-4 text-indigo-600" />
        <span className="truncate max-w-[180px]" data-testid="tenant-switcher-active-name">
          {activeTenant?.tenant_name || 'Select tenant'}
        </span>
        {isPlatformAdmin && (
          <Shield
            data-testid="tenant-switcher-platform-admin-badge"
            className="w-3.5 h-3.5 text-amber-500"
          />
        )}
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
      </button>

      {open && (
        <div
          data-testid="tenant-switcher-menu"
          className="absolute right-0 mt-1 w-72 bg-white rounded-md shadow-lg border border-slate-200 z-50 overflow-hidden"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
            Your tenants
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {memberships.map((m) => {
              const active = m.tenant_id === activeId;
              return (
                <li key={m.tenant_id}>
                  <button
                    data-testid={`tenant-option-${m.tenant_slug}`}
                    onClick={() => { switchTenant(m.tenant_id); setOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 ${active ? 'bg-indigo-50/50' : ''}`}
                  >
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium text-slate-900">{m.tenant_name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{m.tenant_slug}</div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${ROLE_TONE[m.role] || ROLE_TONE.viewer}`}>
                      {m.role}
                    </span>
                    {active && <Check data-testid={`tenant-option-${m.tenant_slug}-check`} className="w-4 h-4 text-indigo-600" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
